import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, message, Modal, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import PageTab from "@/components/i-pageTab/i-pageTab";
import PageTable from "@/components/i-pageTable/i-pageTable";
import { appUserBaseList } from "@/api";
import {
  addDepartmentPerformance,
  deleteDepartmentPerformance,
  departmentPerformanceExport,
  selectCmsDepartmentPerformancePage,
} from "@/api/operation";
import {
  downloadFile,
  getTimeFormat,
  setIcons,
} from "@/utils/common";
import dayjs from "dayjs";

interface AppUserOption {
  label: string;
  value: number;
}

interface AppUserBaseItem {
  mobile: string;
  nickName: string;
  userId: number;
}

interface AppUserBaseResponse {
  data: AppUserBaseItem[];
}

const initialFormData: SelectCmsDepartmentPerformancePageParams = {
  endTime: dayjs().endOf("month").format("YYYY-MM-DD 23:59:59"),
  mobile: "",
  nickName: "",
  startTime: dayjs().startOf("month").format("YYYY-MM-DD 00:00:00"),
  teamAttribute: "",
  ypageNum: 1,
  ypageSize: 10,
};

const isAppUserBaseResponse = (
  response: unknown
): response is AppUserBaseResponse => {
  if (typeof response !== "object" || response === null) {
    return false;
  }

  const data = Reflect.get(response, "data");
  return Array.isArray(data);
};

const DepartmentPerformance = (): React.ReactElement => {
  const [formData, setFormData] =
    useState<SelectCmsDepartmentPerformancePageParams>(initialFormData);
  const [pageData, setPageData] = useState<
    SelectCmsDepartmentPerformancePageRow[]
  >([]);
  const [userOptions, setUserOptions] = useState<AppUserOption[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [addLoading, setAddLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  const getPageData = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await selectCmsDepartmentPerformancePage(formData);
      setPageData(data.rows);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [formData]);

  useEffect(() => {
    void getPageData();
  }, [getPageData]);

  const handleSearch = useCallback(
    (values: SelectCmsDepartmentPerformancePageParams): void => {
      setFormData((current) => ({
        ...current,
        ...values,
        ypageNum: 1,
      }));
    },
    []
  );

  const handleReset = useCallback((): void => {
    setFormData(initialFormData);
  }, []);

  const handlePageChange = useCallback(
    (current: number, pageSize: number): void => {
      setFormData((values) => ({
        ...values,
        ypageNum: current,
        ypageSize: pageSize,
      }));
    },
    []
  );

  const handleOpenAdd = useCallback((): void => {
    setUserOptions([]);
    setOpen(true);
  }, []);

  const handleCloseAdd = useCallback((): void => {
    setOpen(false);
    setUserOptions([]);
  }, []);

  const handleUserSearch = useCallback(async (mobile: string): Promise<void> => {
    if (!mobile.trim()) {
      setUserOptions([]);
      return;
    }

    const response: unknown = await appUserBaseList({ mobile });
    if (!isAppUserBaseResponse(response)) {
      setUserOptions([]);
      return;
    }

    setUserOptions(
      response.data.map((item) => ({
        label: `${item.nickName}（${item.mobile}）`,
        value: item.userId,
      }))
    );
  }, []);

  const handleAdd = useCallback(
    async (values: AddDepartmentPerformanceParams): Promise<void> => {
      setAddLoading(true);
      try {
        await addDepartmentPerformance(values);
        message.success("新增成功");
        handleCloseAdd();
        await getPageData();
      } finally {
        setAddLoading(false);
      }
    },
    [getPageData, handleCloseAdd]
  );

  const handleDelete = useCallback(
    (id?: number): void => {
      if (id === undefined) {
        message.error("列表缺少部门业绩记录ID，暂时无法删除");
        return;
      }

      Modal.confirm({
        title: "系统提示",
        content: "是否确认删除该部门业绩人员？",
        cancelText: "取消",
        okText: "确定",
        onOk: async (): Promise<void> => {
          await deleteDepartmentPerformance(id);
          message.success("删除成功");
          await getPageData();
        },
      });
    },
    [getPageData]
  );

  const handleExport = useCallback(async (): Promise<void> => {
    setExportLoading(true);
    try {
      const data = await departmentPerformanceExport(formData);
      downloadFile(
        data,
        `部门业绩统计${getTimeFormat(new Date(), "yyyy-MM-dd HH:mm:ss")}`
      );
    } finally {
      setExportLoading(false);
    }
  }, [formData]);

  const formTab = useMemo(
    () => [
      {
        label: "用户昵称",
        name: "nickName",
        type: "input",
      },
      {
        label: "用户账号",
        name: "mobile",
        type: "input",
      },
      {
        label: "团队属性",
        name: "teamAttribute",
        type: "input",
      },
      {
        label: "统计时间",
        name: "rangePicker",
        type: "rangePicker",
        defaultValues: [
          dayjs().startOf("month").format("YYYY-MM-DD 00:00:00"),
          dayjs().endOf("month").format("YYYY-MM-DD 23:59:59"),
        ],
        width: "380px",
      },
    ],
    []
  );

  const modalFormTab = useMemo(
    () => [
      {
        label: "用户",
        name: "userId",
        type: "select",
        options: userOptions,
        width: "320px",
        rules: [{ required: true, message: "请选择用户" }],
        params: {
          filterOption: false,
          onSearch: handleUserSearch,
          placeholder: "请输入手机号搜索",
          showSearch: true,
        },
      },
    ],
    [handleUserSearch, userOptions]
  );

  const footer = useMemo(
    () => [
      {
        title: "新增",
        icon: setIcons("PlusOutlined"),
        onClick: handleOpenAdd,
      },
      {
        title: "导出",
        icon: setIcons("DownloadOutlined"),
        loading: exportLoading,
        onClick: handleExport,
      },
    ],
    [exportLoading, handleExport, handleOpenAdd]
  );

  const columns = useMemo<
    ColumnsType<SelectCmsDepartmentPerformancePageRow>
  >(
    () => [
      {
        title: "用户业务ID",
        dataIndex: "userBusinessId",
        key: "userBusinessId",
      },
      {
        title: "用户昵称",
        dataIndex: "nickName",
        key: "nickName",
      },
      {
        title: "用户账号",
        dataIndex: "mobile",
        key: "mobile",
      },
      {
        title: "团队属性",
        dataIndex: "teamAttribute",
        key: "teamAttribute",
      },
      {
        title: "团队购买设备量",
        dataIndex: "teamDeviceCount",
        key: "teamDeviceCount",
      },
      {
        title: "团队业绩",
        dataIndex: "teamPayAmountTotal",
        key: "teamPayAmountTotal",
      },
      {
        title: "添加时间",
        dataIndex: "createTime",
        key: "createTime",
      },
      {
        title: "操作",
        dataIndex: "id",
        key: "operation",
        fixed: "right",
        width: "90px",
        render: (id?: number): React.ReactNode => (
          <Space>
            <Button
              disabled={id === undefined}
              icon={setIcons("DeleteOutlined")}
              onClick={handleDelete.bind(null, id)}
              size="small"
              type="text"
            >
              删除
            </Button>
          </Space>
        ),
      },
    ],
    [handleDelete]
  );

  return (
    <div>
      <Modal
        destroyOnHidden
        footer={null}
        onCancel={handleCloseAdd}
        open={open}
        title="新增部门业绩人员"
        width="420px"
      >
        <PageTab
          confirmLoading={addLoading}
          formTab={modalFormTab}
          reset={handleCloseAdd}
          search={handleAdd}
          subFoot
        />
      </Modal>
      <PageTab
        footer={footer}
        formTab={formTab}
        reset={handleReset}
        search={handleSearch}
      />
      <PageTable
        columns={columns}
        data={pageData}
        loading={loading}
        pageChange={handlePageChange}
        pagination={{
          current: formData.ypageNum,
          pageSize: formData.ypageSize,
          total,
        }}
        rowKey={(row: SelectCmsDepartmentPerformancePageRow): string =>
          String(row.id ?? `user-${row.userId}`)
        }
      />
    </div>
  );
};

export default DepartmentPerformance;
